import htmlKeyboardResponse from "@jspsych/plugin-html-keyboard-response";

import { br, div, p, tag } from "../../lib/markup/tags";
import {
  isTTLDeviceConnected,
  reconnectTTLDevice,
  requestTTLDevice,
  setTTLStatusBadge,
} from "../../lib/webUsbFtdi";

const BUTTON_ID = "ttl-connect-button";
const SKIP_BUTTON_ID = "ttl-skip-button";
const STATUS_ID = "ttl-connect-status";

/**
 * A trial that grants WebUSB access to the FTDI TTL trigger board before any mission runs
 * Browsers only allow requesting device permission from a user gesture, so this shows a button
 * the experimenter must click - unless a device was already authorized in a previous session,
 * in which case it reconnects silently and skips straight through.
 * Also offers a "proceed without TTL" option, for experimenters without the USB hardware, which
 * flips metadata.pulseTTLtrialStart to false so the rest of the run never attempts to pulse or
 * nag about reconnecting.
 * Non-blocking: the experiment continues either way - pulseTTL() no-ops if never connected.
 */
export function buildTTLDeviceConnectTrial(jsPsych, metadata) {
  return {
    type: htmlKeyboardResponse,
    choices: "NO_KEYS",
    data: { name: "ttlDeviceConnect" },
    stimulus: () =>
      div(
        p("Checking for the USB TTL trigger board...", { id: STATUS_ID })
        + tag("button", "Connect TTL Device", { id: BUTTON_ID, class: "jspsych-btn", disabled: true })
        + br()
        + tag("button", "Proceed without using TTL synch pulse", {
          id: SKIP_BUTTON_ID,
          class: "jspsych-btn",
          style: "margin-top: 12px;",
          disabled: true,
        })
      ),
    on_load: () => {
      const button = document.getElementById(BUTTON_ID);
      const skipButton = document.getElementById(SKIP_BUTTON_ID);
      const status = document.getElementById(STATUS_ID);
      const finish = (connected) => jsPsych.finishTrial({ ttlDeviceConnected: connected });

      reconnectTTLDevice()
        .catch(() => false)
        .then((connected) => {
          if (connected) {
            finish(true);
            return;
          }

          status.textContent = "Connect the USB TTL trigger board, then click below.";
          button.disabled = false;
          skipButton.disabled = false;
          button.addEventListener("click", async () => {
            button.disabled = true;
            skipButton.disabled = true;
            button.textContent = "Connecting...";
            try {
              await requestTTLDevice();
            } catch (err) {
              console.error("[TTL] device connection failed:", err);
            }
            const connectedNow = isTTLDeviceConnected();
            // Still wants TTL sync but proceeding unconnected (no device picked, or picker
            // cancelled) - keep the experimenter aware for the rest of the run.
            if (!connectedNow) setTTLStatusBadge(false);
            finish(connectedNow);
          });
          skipButton.addEventListener("click", () => {
            metadata.pulseTTLtrialStart = false;
            finish(false);
          });
        });
    },
  };
}
